import { FC, FormEvent, useState } from 'react';
import { Button } from '@/components/ui';
import api from '@/utils/api';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { getErrorMessage } from '@/utils/error';

interface SendGridConfigFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const SendGridConfigForm: FC<SendGridConfigFormProps> = ({ onSuccess, onCancel }) => {
  const [apiKey, setApiKey] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const handleVerify = async () => {
    setError('');
    setSuccess('');

    if (!apiKey || !fromEmail) {
      setError('API Key and From Email are required for verification');
      return;
    }

    try {
      setIsVerifying(true);
      const response = await api.post('/email-config/verify', {
        providerType: 'sendgrid',
        isActive: true,
        sendgridApiKey: apiKey,
        sendgridFromEmail: fromEmail,
        sendgridSenderName: senderName,
      });

      if (response.data.success) {
        setSuccess(response.data.message);
      } else {
        setError(response.data.message);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!apiKey || !fromEmail) {
      setError('API Key and From Email are required');
      return;
    }

    try {
      setIsSaving(true);
      await api.post('/email-config', {
        providerType: 'sendgrid',
        isActive: true,
        sendgridApiKey: apiKey,
        sendgridFromEmail: fromEmail,
        sendgridSenderName: senderName,
      });
      onSuccess();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Alert Banners */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md flex items-start gap-2 text-green-700 text-sm">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            API Key <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="SG.xxxxxxxxxxxxx"
            className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm placeholder-gray-400
            focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
            disabled:bg-gray-50"
            required
            disabled={isSaving}
          />
          <p className="text-xs text-gray-500 mt-1">
            You can generate an API key in your <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">SendGrid Dashboard</a>.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="noreply@domain.com"
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm placeholder-gray-400
                focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              required
              disabled={isSaving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sender Name
            </label>
            <input
              type="text"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="e.g. Acme Corp"
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm placeholder-gray-400
                focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              disabled={isSaving}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isSaving || isVerifying}
          className="text-gray-600 hover:bg-gray-100"
        >
          Cancel
        </Button>

        <Button
          type="button"
          variant="secondary"
          onClick={handleVerify}
          disabled={isSaving || isVerifying || !apiKey || !fromEmail}
          className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          {isVerifying ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Checking...
            </>
          ) : (
            'Check Connection'
          )}
        </Button>

        <Button
          type="submit"
          variant="primary"
          isLoading={isSaving}
          disabled={isSaving || isVerifying}
          className="bg-gray-900 text-white hover:bg-black"
        >
          Save Configuration
        </Button>
      </div>
    </form>
  );
};
